import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaSearch, FaBars, FaTimes, FaUser, FaShoppingBag, FaChevronDown } from 'react-icons/fa';
import { useBasket } from './BasketContext';
import { useServiceCatalog } from './ServiceCatalogContext';
import { useAuth } from './AuthContext';

const HeaderContainer = styled.header`
  padding: 1rem 2rem;
  border-bottom: 1px solid #eee;
  background-color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative; /* Needed for z-index to work on children */
  z-index: 101; /* Ensure header is above other content */

  @media (max-width: 768px) {
    flex-wrap: wrap; /* Allow items to wrap onto the next line on mobile */
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  color: #222;
  text-decoration: none;
  height: 50px; /* Adjust height as needed */

  @media (max-width: 768px) {
    order: 1; /* Keep logo on the left */
    flex-grow: 1; /* Allow logo to take up space to push hamburger to the right */
  }
`;

const LogoImage = styled.img`
  height: 100%;
  width: auto;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-grow: 1;
  margin: 0 2rem;
  max-width: 500px;

  @media (max-width: 768px) {
    order: 4; /* Move search bar to the second row */
    width: 100%;
    margin: 1rem 0 0 0; /* Adjust margin for wrapped layout */
    max-width: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem; /* Left padding for the icon */
  border: 1px solid #e0e0e0;
  border-radius: 25px; /* Pill shape */
  font-size: 1rem;
  background-color: #f8f9fa;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #005eb8;
    box-shadow: 0 0 0 2px rgba(0, 94, 184, 0.2);
  }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 1rem;
  color: #6c757d;
`;

const SearchResultsContainer = styled.div`
  position: absolute;
  top: calc(100% + 5px); /* Position below the search bar with a small gap */
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 400px;
  overflow-y: auto;
`;

const SearchResultItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: #333;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8f9fa;
  }
`;

const SearchResultImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 1rem;
`;

const SearchResultInfo = styled.div``;

const SearchResultName = styled.div`
  font-weight: 600;
`;

const SearchResultPrice = styled.div`
  font-size: 0.9rem;
  color: #555;
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    order: 2; /* Position this group to the right of the logo on mobile */
  }
`;

const ActionIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1.2rem;
  }
`;

const IconLink = styled(Link)`
  color: #333;
  font-size: 1.3rem;
  position: relative;
  display: flex;
  align-items: center;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #005eb8;
  }
`;

const IconButton = styled.div`
  color: #333;
  font-size: 1.3rem;
  position: relative;
  display: flex;
  align-items: center;
  transition: color 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    color: #005eb8;
  }
`;

const BasketCount = styled.span`
  background-color: #005eb8;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
  position: absolute;
  top: -8px;
  right: -8px;
  border: 1px solid white;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;

  /* Direct children only - a plain "a" here also matches dropdown links
     nested deep inside NavItem, pushing them right with an unwanted margin. */
  > a {
    margin-left: 1.5rem;
    text-decoration: none;
    color: #333;
    &:hover {
      text-decoration: underline;
    }
  }

  /* Hide the desktop navigation on mobile screens */
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-left: 1.5rem; /* Own the spacing here, outside the box, so the dropdown (positioned off this box) sits flush under the link. */

  & > a {
    margin-left: 0; /* Avoid double-spacing; NavLink's own margin-left would otherwise sit inside this box and offset the dropdown. */
  }

  &:hover > div {
    display: block;
  }
`;

const IconNavItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &:hover > div {
    display: block;
  }
`;

const NavLink = styled(Link)`
  margin-left: 1.5rem;
  text-decoration: none;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0; /* Add some padding for easier hover */
  &:hover {
    color: #005eb8;
  }
`;

const DropdownMenu = styled.div`
  display: none;
  opacity: 0;
  position: absolute; /* Position it relative to its parent (the NavItem). */
  top: 100%; /* Place it directly below the parent. */
  left: 0; /* Align the dropdown's left edge with the trigger, so it never overhangs the item to its left. */
  background-color: white; /* Give it a solid white background. */
  box-shadow: 0 5px 16px rgba(0,0,0,0.1); /* Add a subtle shadow for a "floating" effect. */
  border-radius: 8px; /* Round the corners for a modern look. */
  padding: 0.5rem 0; /* Vertical breathing room only; items span full width. */
  overflow: hidden; /* Clip item hover backgrounds to the rounded corners. */
  z-index: 10; /* Ensure the dropdown appears on top of other content. */
  min-width: 220px; /* Set a minimum width to keep it looking good. */
  border: 1px solid #eee; /* A light border for definition. */
  transition: opacity 0.2s ease-in-out; /* Animate the opacity for a smooth fade effect. */
  list-style: none; /* Remove default bullet points */
  margin: 0; /* Reset any default margins */
  ${NavItem}:hover & { display: block; opacity: 1; }
`;

const AccountDropdownMenu = styled(DropdownMenu)`
  min-width: 180px;

  /* When the parent IconNavItem is hovered, make this DropdownMenu appear. */
  ${IconNavItem}:hover & {
    display: block;
    opacity: 1;
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 0.75rem 1.5rem; /* Apply consistent padding all around. */
  color: #555;
  text-decoration: none;
  font-size: 0.95rem;
  white-space: nowrap;
  text-align: left; /* Align text to the left by default. */
  &:hover {
    background-color: #f8f9fa;
    color: #005eb8;
  }
`;

const DropdownButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem 1.5rem;
  color: #333;
  text-decoration: none;
  font-size: 0.95rem;
  white-space: nowrap;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background-color: #f8f9fa;
    color: #005eb8;
  }
`;

const Hamburger = styled.div`
  display: none; /* Hidden by default */
  cursor: pointer;
  font-size: 1.3rem; /* Match other icon sizes */
  color: #333;

  /* Show the hamburger icon only on mobile screens */
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileNavLink = styled(Link)`
  font-size: 1.5rem;
  margin: 1.2rem 0;
  color: #333;
  text-decoration: none;
`;

const MobileNavButton = styled.div`
  font-size: 1.5rem;
  margin: 1.2rem 0;
  color: #333;
  text-decoration: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start; /* Align items to the top */
  background-color: white;
  padding-top: 9rem; /* Increased padding-top for more space */
  position: fixed; /* Full-screen overlay */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  /* Control visibility with a transform */
  transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(-100%)')};
  transition: transform 0.3s ease-in-out; /* Smooth transition for the slide-in effect */

  /* Close button inside mobile nav */
  & > ${Hamburger} {
    display: block;
    position: absolute;
    top: 1.5rem;
    right: 2rem;
    font-size: 2rem;
  }
`;

const SubMenu = styled.div`
  display: ${({ open }) => (open ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: #f8f9fa;
  padding: 0.75rem 0;
  margin-top: -1.2rem;
  margin-bottom: 1.2rem;
  position: ${({ open }) => (open ? 'relative' : 'absolute')};
  visibility: ${({ open }) => (open ? 'visible' : 'hidden')};
`;

const SubMenuItem = styled(Link)`
  font-size: 1.2rem;
  margin: 0.6rem 0;
  color: #555;
  text-decoration: none;
`;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const { itemCount } = useBasket();
  const { allServices, serviceCategories } = useServiceCatalog();
  const [servicesOpen, setServicesOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    // If we are closing the main menu, also close any sub-menus
    if (isOpen) {
      setServicesOpen(false);
    }
  };

  const toggleServices = (e) => {
    e.preventDefault();
    setServicesOpen(!servicesOpen);
  };

  const closeAllMenus = () => {
    setIsOpen(false);
    setServicesOpen(false);
  };

  const handleLogout = () => {
    closeAllMenus();
    logout();
    navigate('/');
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.trim() === '') {
      setSearchResults([]);
      return;
    }

    const filteredServices = allServices.filter(service =>
      service.name.toLowerCase().includes(query.toLowerCase())
    );
    setSearchResults(filteredServices);
  };

  const closeSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  // Close search results when the input loses focus, with a small delay
  // to allow a click on a result item to register.
  const handleSearchBlur = () => {
    setTimeout(() => {
      setSearchResults([]);
    }, 200);
  };

  return (
    <>
      <HeaderContainer>
        <LogoLink to="/" aria-label="The Letter Doctor, Homepage">
          <LogoImage src="/logo.svg" alt="" />
        </LogoLink>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="search"
            placeholder="Search for services..."
            value={searchQuery}
            onChange={handleSearchChange}
            onBlur={handleSearchBlur}
          />
          {searchResults.length > 0 && (
            <SearchResultsContainer>
              {searchResults.map(service => (
                <SearchResultItem key={service.id} to={service.path} onClick={closeSearch}>
                  <SearchResultImage src={service.image} alt={service.name} />
                  <SearchResultInfo>
                    <SearchResultName>{service.name}</SearchResultName>
                    <SearchResultPrice>£{service.price.toFixed(2)}</SearchResultPrice>
                  </SearchResultInfo>
                </SearchResultItem>
              ))}
            </SearchResultsContainer>
          )}
        </SearchContainer>
        <RightSide>
          <ActionIcons>
            {isAuthenticated ? (
              <IconNavItem>
                <IconButton aria-label="My Account Menu">
                  <FaUser />
                </IconButton>
                <AccountDropdownMenu>
                  <DropdownItem to="/account" onClick={closeAllMenus}>My Orders</DropdownItem>
                  <DropdownItem to="/account/details" onClick={closeAllMenus}>My Details</DropdownItem>
                  <DropdownButton onClick={handleLogout}>Log out</DropdownButton>
                </AccountDropdownMenu>
              </IconNavItem>
            ) : (
              <IconLink to="/login" aria-label="Login">
                <FaUser />
              </IconLink>
            )}
            <IconLink to="/basket" aria-label="Shopping Basket">
              <FaShoppingBag />
              {itemCount > 0 && <BasketCount>{itemCount}</BasketCount>}
            </IconLink>
          </ActionIcons>
          <Nav>
            <NavLink to="/" onClick={closeAllMenus}>Home</NavLink>
            <NavItem>
              <NavLink to="/services" onClick={(e) => e.preventDefault()}>Services <FaChevronDown size="0.7em" /></NavLink>
              <DropdownMenu>
                {serviceCategories.map(category => (
                  <DropdownItem key={category.id} to={category.path} onClick={closeAllMenus}>
                    {category.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </NavItem>
            <NavLink to="/about" onClick={closeAllMenus}>About Us</NavLink>
            <NavLink to="/contact" onClick={closeAllMenus}>Contact Us</NavLink>
          </Nav>
          <Hamburger onClick={toggleMenu}>{isOpen ? <FaTimes /> : <FaBars />}</Hamburger>
        </RightSide>
      </HeaderContainer>
      <MobileNav open={isOpen}>
        <Hamburger onClick={toggleMenu}><FaTimes /></Hamburger>
        <MobileNavLink to="/" onClick={closeAllMenus}>Home</MobileNavLink>
        <MobileNavButton onClick={toggleServices}>
          Services <FaChevronDown size="0.7em" />
        </MobileNavButton>
        <SubMenu open={servicesOpen}>
          {serviceCategories.map(category => (
            <SubMenuItem key={category.id} to={category.path} onClick={closeAllMenus}>
              {category.name}
            </SubMenuItem>
          ))}
        </SubMenu>
        <MobileNavLink to="/about" onClick={closeAllMenus}>About Us</MobileNavLink>
        <MobileNavLink to="/contact" onClick={closeAllMenus}>Contact Us</MobileNavLink>
      </MobileNav>
    </>
  );
};

export default Header;